<template>
  <div
    ref="container"
    :class="['base-overlay-editor', { 'auto-grow': autoGrow, 'bullet': bullet }]"
  >
    <!-- ハイライト用 overlay -->
    <pre
      ref="overlay"
      class="base-overlay-editor__overlay"
      v-html="convertedText"
    />

    <!-- 入力用 textarea -->
    <textarea
      :id="textareaNodeId"
      ref="textarea"
      class="base-overlay-editor__textarea"
      :value="textValue"
      spellcheck="false"
      @click.prevent="onClick"
      @scroll="onScroll"
      @input="onInput"
    />
  </div>
</template>

<script>
/**
 * テキストエリアにハイライトを付けるためのコンポーネント
 * - ハイライト箇所は suggestions で指定
 * - selectedSuggestionId で選択中のハイライトを強調表示
 * - autoGrow が true の場合、入力内容に合わせて自動拡張
 * - bullet が true の場合、行頭に「・」を付ける
 */
export default {
  name: "BaseOverlayEditor",
  props: {
    modelValue: {
      type: String,
      default: ""
    },
    suggestions: {
      type: Array,
      default: () => []
    },
    selectedSuggestionId: {
      type: Number,
      default: null
    },
    // 固定高さ（autoGrow = false のときの初期値）
    height: {
      type: Number,
      default: 600
    },
    // true の場合、入力内容に合わせて自動拡張する
    autoGrow: {
      type: Boolean,
      default: false
    },
    // true の場合、行頭に「・」を付ける
    bullet: {
      type: Boolean,
      default: false
    },
    textareaNodeId: {
      type: String,
      default: null
    }
  },
  emits: ["update:modelValue", "select-suggestion"],
  computed: {
    textValue() {
      return this.modelValue;
    },
    /**
     * 1) 指摘箇所を <span> でハイライト
     * 2) 行ごとに <div> に分割してCSSで行頭に「・」を付ける
     */
    convertedText() {
      if (!this.textValue) return "";

      // ---- 1) まずハイライト処理を行う ----
      let replaced = this.textValue;
      this.suggestions.forEach(suggestion => {
        const classes = ["base-overlay-editor__highlight"];
        if (suggestion.id === this.selectedSuggestionId) {
          classes.push("is-selected");
        }
        if (!suggestion.quote) return;
        replaced = replaced.replace(
          suggestion.quote,
          `<span class="${classes.join(" ")}" data-suggestion-id="${suggestion.id}">${suggestion.quote}</span>`
        );
      });

      // ---- 2) bullet が true の場合のみ改行で分割し、<div class="editor-line">…</div> へ変換 ----
      if (this.bullet) {
        const lines = replaced.split("\n");
        const wrapped = lines
          .map(line => {
            // 空行なら、見た目上高さを確保するため &nbsp; を入れておく
            if (line.trim() === "") {
              return "<div class=\"editor-line\">&nbsp;</div>";
            }
            return `<div class="editor-line">${line}</div>`;
          })
          .join("");

        return wrapped;
      }

      return replaced;
    }
  },
  watch: {
    // selectedSuggestionId の変更時にスクロールを調整
    selectedSuggestionId(newVal) {
      if (!newVal) return;
      this.$nextTick(() => {
        this.scrollToSelectedSuggestion(newVal);
      });
    }
  },
  mounted() {
    // 初期描画時もテキストがあるなら高さ調整
    if (this.autoGrow) {
      this.$nextTick(() => {
        this.adjustHeight();
      });
    } else {
      // 高さ固定の場合、textarea & overlay & container に高さを適用
      this.$refs.textarea.style.height = `${this.height}px`;
      this.$refs.overlay.style.height = `${this.height}px`;
      this.$refs.container.style.height = `${this.height}px`;
    }
  },
  methods: {
    onScroll() {
      const textarea = this.$refs.textarea;
      const overlay = this.$refs.overlay;
      overlay.scrollTop = textarea.scrollTop;
    },
    onInput(event) {
      // v-model で双方向バインディングさせるためのイベント発火
      this.$emit("update:modelValue", event.target.value);

      // autoGrow 時の高さ再計算
      if (this.autoGrow) {
        this.adjustHeight();
      }
    },
    /**
     * テキスト/ハイライトの行数に合わせて高さを伸縮
     */
    adjustHeight() {
      const textarea = this.$refs.textarea;
      const overlay = this.$refs.overlay;

      // 1) いったん auto でリセット
      textarea.style.height = "auto";
      overlay.style.height = "auto";

      // 2) scrollHeight を取得して高さを適用
      const newHeight = textarea.scrollHeight + 10;
      textarea.style.height = `${newHeight}px`;
      overlay.style.height = `${newHeight}px`;
    },
    onClick(event) {
      const x = event.clientX;
      const y = event.clientY;

      // NOTE: この下3行の処理は、強制的なレイアウト再描画をブラウザに強いるためにかなり重いが、他にtextarea下の要素を取得する手段がない
      this.$refs.textarea.style.visibility = "hidden";
      const element = document.elementFromPoint(x, y);
      this.$refs.textarea.style.visibility = "visible";
      if (!element || !element.dataset.suggestionId) return;

      const suggestionId = Number(element.dataset.suggestionId);
      this.$emit("select-suggestion", suggestionId);
    },
    // ハイライト要素を可視範囲にスクロール
    scrollToSelectedSuggestion(suggestionId) {
      const overlay = this.$refs.overlay;
      const textarea = this.$refs.textarea;
      if (!overlay || !textarea) return;

      const suggestionEl = overlay.querySelector(`span[data-suggestion-id="${suggestionId}"]`);
      if (!suggestionEl) return;

      this._scrollMainContainerIfNeeded();

      const isVisible = this._isSuggestionVisible(overlay, suggestionEl);
      if (!isVisible) {
        this._scrollSuggestionIntoView(overlay, textarea, suggestionEl);
      }
    },    
    // エディタ本体が外側コンテナの表示範囲外なら、ラベル表示ぶん余白を残してスクロール
    _scrollMainContainerIfNeeded() {
      const container = this.$refs.container;
      const mainContainer = document.querySelector(".l-anytime-consultation__main");
      if (!container || !mainContainer) return;

      const containerRect = container.getBoundingClientRect();
      const mainRect = mainContainer.getBoundingClientRect();
      const isContainerVisible =
        containerRect.top >= mainRect.top &&
        containerRect.bottom <= mainRect.bottom;

      if (isContainerVisible) return;

      const topOffset = 30;
      const nextScrollTop =
        mainContainer.scrollTop + (containerRect.top - mainRect.top) - topOffset;

      mainContainer.scrollTo({
        top: Math.max(0, nextScrollTop),
        behavior: "smooth"
      });
    },
    // ハイライト要素が overlay の可視範囲内かどうかを判定
    _isSuggestionVisible(overlay, suggestionEl) {
      const spanPosition = suggestionEl.offsetTop;
      const spanHeight = suggestionEl.offsetHeight;
      const divScrollTop = overlay.scrollTop;
      const divHeight = overlay.clientHeight;

      return (
        spanPosition >= divScrollTop &&
        spanPosition + spanHeight <= divScrollTop + divHeight
      );
    },
    // ハイライト要素が中央に来るよう overlay / textarea を同期スクロール
    _scrollSuggestionIntoView(overlay, textarea, suggestionEl) {
      const spanPosition = suggestionEl.offsetTop;
      const spanHeight = suggestionEl.offsetHeight;
      const divHeight = overlay.clientHeight;
      const spanCenter = spanPosition + spanHeight / 2;
      const divCenter = divHeight / 2;
      const scrollPosition = spanCenter - divCenter;

      overlay.scrollTo({ top: scrollPosition, behavior: "smooth" });
      textarea.scrollTo({ top: scrollPosition, behavior: "smooth" });
    },
  }
};
</script>

<style lang="scss">
.base-overlay-editor {
  position: relative;
  width: 100%;

  &__overlay {
    position: absolute;
    width: 100%;
    background-color: #e8f2ff;
    color: transparent; // 重なりをテストする場合はここをコメントアウト
    border-radius: 4px;
    font-family: Roboto, sans-serif;
    white-space: pre-wrap;
    border: 2px solid transparent;
    padding: 10px 12px 50px 12px; // なぜか左と上が2px分だけズレるのでtextareaのpaddingに加算
    overflow: auto;
  }

  &__textarea {
    position: relative;
    width: 100%;
    border: 2px solid rgb(118, 118, 118);
    border-radius: 4px;
    font-family: Roboto, sans-serif;
    white-space: pre-wrap;
    padding: 10px 12px 10px 12px;
    overflow: auto;
    resize: none;
  }

  &__highlight {
    border-bottom: 1px dashed #EA1537;
    padding-bottom: 1px;
    margin-bottom: -3px;

    &.is-selected {
      background: rgba(234, 21, 55, 0.2);
    }
  }

  .editor-line {
    display: block;
    position: relative;
    padding-left: 1em;

    &::before {
      color: #666;
      position: absolute;
      content: "・";
      left: 0;
    }
  }

  &.auto-grow {

    .base-overlay-editor__overlay,
    .base-overlay-editor__textarea {
      height: auto;
      overflow: hidden;
    }
  }

  &.bullet {
    .base-overlay-editor__textarea {
      padding-left: calc(1em + 12px);
    }
  }
}
</style>